import axios from "../../../../api/axios";

export const FACILITY_INVENTORY_ENDPOINTS = {
  GET_PROPERTIES: "v2/master_sheet/property-list",
  BASE: "v2/inventory/facility",
  UPLOAD_INVOICE: "v2/inventory/facility/upload-invoice",
  GET_INVOICE_SIGNED_URL: "v2/inventory/facility/invoice-signed-url",
  MOVE_ITEM: "v2/inventory/facility/move",
  GENERATE_REPORT: "v2/inventory/facility/generate-report",
};

/**
 * Central error handler
 */
const handleApiError = (error, defaultMessage) => {
  const message = error?.response?.data?.message || error.message || defaultMessage;
  throw new Error(message);
};

/**
 * Create new facility inventory items
 */
export const createFacilityItem = async (payload) => {
  try {
    const response = await axios.post(FACILITY_INVENTORY_ENDPOINTS.BASE, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create facility inventory item");
  }
};

/**
 * Update an existing facility inventory item
 */
export const updateFacilityItem = async (payload) => {
  try {
    const response = await axios.put(FACILITY_INVENTORY_ENDPOINTS.BASE, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update facility inventory item");
  }
};

/**
 * Delete a facility inventory item
 */
export const deleteFacilityItem = async (id) => {
  try {
    const url = `${FACILITY_INVENTORY_ENDPOINTS.BASE}/${id}`;
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete facility inventory item");
  }
};

/**
 * Fetch all facility inventory items
 */
export const getAllFacilityItems = async (property_id, building_name) => {
  try {
    const response = await axios.get(FACILITY_INVENTORY_ENDPOINTS.BASE, {
      params: { property_id, building_name },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch facility inventory items");
  }
};

/**
 * Upload invoice document for a facility item
 */
export const uploadInvoice = async (property_id, formData, onUploadProgress) => {
  try {
    const url = `${FACILITY_INVENTORY_ENDPOINTS.UPLOAD_INVOICE}?property_id=${property_id}`;
    const response = await axios.post(url, formData, {
      onUploadProgress,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to upload invoice");
  }
};

/**
 * Fetch signed URL for a specific invoice of a facility inventory item
 */
export const getInvoiceSignedUrl = async (file_url) => {
  try {
    const response = await axios.get(FACILITY_INVENTORY_ENDPOINTS.GET_INVOICE_SIGNED_URL, {
      params: { file_url },
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch invoice signed URL");
  }
};

/**
 * Fetches property list based on user role and ID
 * @param {string} role - User role
 * @param {string} id - User ID
 * @returns {Promise<Object>} Filtered property data
 */
export const getPropertyList = async (role, id) => {
  try {
    const { data } = await axios.get(
      `${FACILITY_INVENTORY_ENDPOINTS.GET_PROPERTIES}?role=${role || ""}&id=${id || ""}`
    );
    const properties = data.data || [];
    return {
      propertyList: properties,
      building_list: properties.length ? properties[0].building || [] : [],
    };
  } catch (error) {
    handleApiError(error, "Failed to fetch property list");
  }
};

/**
 * Move a facility inventory item (or specified quantity) to another property
 * @param {Object} payload - The payload containing item_id, destination_property_id, destination_building_name, and optional quantity
 * @returns {Promise<Object>} The response data from the server
 */
export const moveFacilityItem = async (payload) => {
  try {
    const response = await axios.post(FACILITY_INVENTORY_ENDPOINTS.MOVE_ITEM, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to move facility inventory item");
  }
};

/**
 * Generate a facility inventory report
 * @param {Object} payload - The payload containing property_id, building_name, and optional filters (itemName, modelNo, category, location, status)
 * @returns {Promise<Object>} The response data from the server
 */
export const generateFacilityReport = async (payload, config = {}) => {
  try {
    const response = await axios.post(FACILITY_INVENTORY_ENDPOINTS.GENERATE_REPORT, payload, {
      ...config,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to generate facility report");
    throw error; // Re-throw to handle in the caller
  }
};